import React from 'react';
import { Pressable, Text as RNText, View } from 'react-native';
import { useSendbirdChat } from '@sendbird/uikit-react-native';
import { Text } from '@shared/components/text';
import { useI18n } from '@shared/i18n/useI18n';
import { CHAT_ROW } from '../../constants';
import { useChannelPreviewMessage } from '../../hooks/useChannelPreviewMessage';
import { useChannelTitle } from '../../hooks/useChannelTitle';
import { useMessageLabels } from '../../hooks/useMessageLabels';
import type { TChatChannelRowProps } from '../../types';
import { getChannelPreview } from '../../utils/channelPreview';
import { getChannelKind } from '../../utils/channelType';
import { formatChatTimestamp } from '../../utils/formatChatTimestamp';
import { getDisplayedUnreadCount } from '../../utils/unreadChannels';
import { ChatChannelAvatar } from './ChatChannelAvatar';
import { ChatUnreadBadge } from './ChatUnreadBadge';

export const ChatChannelRow = ({ channel, onPress }: TChatChannelRowProps) => {
    const { t } = useI18n();
    const labels = useMessageLabels();
    const title = useChannelTitle(channel);
    const { currentUser } = useSendbirdChat();

    const previewMessage = useChannelPreviewMessage(channel);
    const unreadCount = getDisplayedUnreadCount(channel);
    const isUnread = unreadCount > 0;

    const preview = getChannelPreview(previewMessage, currentUser?.userId, labels);
    const timestamp = previewMessage
        ? formatChatTimestamp(previewMessage.createdAt, { yesterday: t('screens.chat.yesterday') })
        : null;

    return (
        <Pressable
            onPress={onPress}
            style={{ height: CHAT_ROW.height }}
            className="flex-row items-center px-md gap-sm border-b border-gray-300 bg-white active:bg-gray-200"
        >
            <ChatChannelAvatar kind={getChannelKind(channel)} />
            <View className="flex-1 min-w-0 gap-1">
                <Text numberOfLines={1} className={`text-md ${isUnread ? 'font-bold' : 'font-normal'}`}>
                    {title}
                </Text>
                {preview !== null && (
                    <Text numberOfLines={1} className="text-md text-gray-500">
                        {preview.prefix !== null && <RNText className="font-bold">{preview.prefix} </RNText>}
                        {preview.body}
                    </Text>
                )}
            </View>
            {(timestamp !== null || isUnread) && (
                <View className="items-center gap-1 shrink-0">
                    {timestamp !== null && <Text className="text-sm text-gray-500">{timestamp}</Text>}
                    {isUnread && <ChatUnreadBadge count={unreadCount} />}
                </View>
            )}
        </Pressable>
    );
};
